import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import _ from "lodash";

import canvasStore, {
  linkProperty,
  linkTextProperty,
  setPropertyMultiplier,
  unLinkProperty,
  updateProperties,
  updateRule,
  addRule
} from "../canvas/store";
import { flattenDB, flattenDBInfo, linkableFields } from "../spreadsheet/store";
import { RuleComparator } from "../spreadsheet/linker";
import { propertyType, propertyToInputType, inputComponent, SelectInput } from "./input";

import { InputUI, ModalTitle, Box } from "../reusable/components";
import { Divider } from "../reusable/layout";

const SelectUI = styled.div`
  display: inline-block;
  position: relative;
  top: 10px;
`;

const Highlight = styled.span`
  font-weight: bold;
  text-transform: ${p => p.cap && "capitalize"};
  word-spacing: normal;
`;

const Story = styled.div`
  word-spacing: 8px;
  line-height: 35px;
  padding: 0px 15px;
`;

const InputWrapper = styled.div`
  display: inline-block;
  top: 7px;
  position: ${p => p.align && "relative"};
`;

const Selector = ({ value, options, onChange, w }) => {
  return (
    <SelectUI w={w}>
      <SelectInput width={w} value={value} options={options} onChange={onChange} />
    </SelectUI>
  );
};

const selectedProp = p => canvasStore.selectedBlock[p];

const sampleTemplate = "Hello I'm {twitch.name}";

const updateFonction = p => (propertyType(p) === "text" ? linkTextProperty : linkProperty);
const isTextTemplate = v =>
  typeof v === "string" &&
  v.includes("{") &&
  v.includes("}") &&
  v.replace("{", "").replace("}", "");

const StoryText = ({ property }) => (
  <div>
    using a template like <Highlight>"{sampleTemplate}"</Highlight>
    <br />
    <InputUI
      width="50%"
      type="text"
      value={selectedProp(property)}
      onChange={e => updateProperties(canvasStore.selected, { [property]: e.target.value })}
    />
  </div>
);

const StoryNumber = ({ property }) => (
  <div>
    using a <Highlight>multiplier</Highlight> of{" "}
    <InputUI
      width="60px"
      type="number"
      min={0}
      step={0.1}
      value={selectedProp(property).multiplier || 1}
      onChange={e => setPropertyMultiplier(canvasStore.selected, property, Number(e.target.value))}
    />
    <br />
    {selectedProp(property).target && (
      <div>
        Meaning that {100 / (selectedProp(property).multiplier || 1)}{" "}
        <Highlight>{selectedProp(property).target}</Highlight> equals 100 {property}
      </div>
    )}
  </div>
);

const StoryDetail = props => {
  const Compo = {
    text: StoryText,
    number: StoryNumber
  }[propertyType(props.property)];
  return Compo ? <Compo {...props} /> : <span />;
};

const Conjuctions = {
  [RuleComparator.Equal]: "to",
  [RuleComparator.Greater]: "than",
  [RuleComparator.Less]: "than",
  [RuleComparator.Between]: ""
};

const Rule = observer(({ cell, comparator, constant, value, property, index }) => {
  const InputProp = propertyToInputType(property);
  const InputCell = inputComponent[flattenDBInfo.get()[cell].type];
  var g = _.curry(updateRule);
  const ruleUpdator = g(canvasStore.selected, property, index);
  return (
    <div>
      if{" "}
      <Selector
        value={cell}
        options={Object.keys(flattenDB.get())}
        onChange={v => ruleUpdator("cell", v)}
        w="120px"
      />{" "}
      is{" "}
      <Selector
        value={comparator}
        options={_.values(RuleComparator)}
        onChange={v => ruleUpdator("comparator", v)}
      />{" "}
      {Conjuctions[comparator]}
      <InputCell
        min={-200}
        value={comparator === RuleComparator.Between ? constant[0] : constant}
        onChange={v =>
          ruleUpdator("constant", RuleComparator.Between === comparator ? [v, constant[1]] : v)}
      />
      {comparator === RuleComparator.Between && (
        <span>
          {" "}
          and{" "}
          <InputCell
            value={constant[1]}
            onChange={v => ruleUpdator("constant", [constant[0], v])}
          />
        </span>
      )}
      <br />
      set <Highlight cap>{property}</Highlight> to{" "}
      <InputWrapper align={propertyType(property) === "color"}>
        <InputProp
          border
          value={value === undefined ? "" : value}
          onChange={v => ruleUpdator("value", v)}
        />
      </InputWrapper>
      <Divider h="15px" />
    </div>
  );
});

const Rules = observer(({ property }) => {
  return (
    <div>
      {canvasStore.selectedBlock.rules &&
        canvasStore.selectedBlock.rules.filter(r => r.property === property).map((r, i) => {
          return <Rule key={i} index={i} {...r} />;
        })}
      <p />
      <div>
        <button onClick={e => addRule(canvasStore.selected, property)}>Add rule</button>
      </div>
    </div>
  );
});

const sortWith_ = (a, b) => {
  if (a.startsWith("_") && b.startsWith("_")) {
    return a.substr(1).localeCompare(b.substr(1));
  } else if (a.startsWith("_")) {
    return 1;
  } else if (b.startsWith("_")) {
    return -1;
  } else {
    return a.localeCompare(b);
  }
};

export default observer(({ property }) => {
  return (
    <div>
      <Box>
        <ModalTitle>
          <h2>Link</h2>
          <Divider h="10px" />
          <p>
            Select a target in your database if you want to link this property to a dynamic value
          </p>
        </ModalTitle>
        <Divider h="15px" />
        <Story>
          Link <Highlight cap>{property}</Highlight> to{" "}
          <Selector
            w={200}
            onChange={v =>
              v === "Nothing"
                ? unLinkProperty(canvasStore.selected, property)
                : updateFonction(property)(canvasStore.selected, property, v)}
            value={
              selectedProp(property).target || isTextTemplate(selectedProp(property)) || "Nothing"
            }
            options={["Nothing"].concat(linkableFields.get().sort(sortWith_))}
          />
          <StoryDetail property={property} />
        </Story>
      </Box>
      <Divider h="30px" />
      <Box>
        <ModalTitle>
          <h2>Rules</h2>
          <Divider h="10px" />
          <p>Rules allow you to change this property based on any value of the database</p>
        </ModalTitle>
        <Divider h="15px" />
        <Story>
          <Rules property={property} />
        </Story>
      </Box>
    </div>
  );
});



// WEBPACK FOOTER //
// ./src/properties/linkui.js